import React, { useEffect, useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import { addYears, endOfYear, startOfYear } from 'date-fns';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { getTicketStatsForParkingLocation } from 'api/canpark/tickets/getTicketStatsForParkingLocation';
import { useTicketTypeMeta } from 'containers/pages/CANpark/context/TicketTerminologyProvider';

interface Slice {
	id: number;
	label: string;
	value: number;
	color: string;
}

const CHART_HEIGHT = 300;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const CustomPieChart: React.FC<{ parkingLocation: ParkingLocation }> = ({
	parkingLocation,
}) => {
	const getTicketTypeMeta = useTicketTypeMeta();
	const [yearOffset, setYearOffset] = useState(0);
	const [data, setData] = useState<Slice[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);
				const startDate = startOfYear(addYears(new Date(), -yearOffset));
				const endDate = endOfYear(startDate);
				const stats = await getTicketStatsForParkingLocation(
					parkingLocation.id,
					startDate,
					endDate,
				);
				if (cancelled) return;
				setData([
					{
						id: 0,
						label: cap(getTicketTypeMeta('log').pluralLabel),
						value: parseInt(stats.logTickets),
						color: getTicketTypeMeta('log').hexColor,
					},
					{
						id: 1,
						label: cap(getTicketTypeMeta('logAndWarn').pluralLabel),
						value: parseInt(stats.logAndWarnTickets),
						color: getTicketTypeMeta('logAndWarn').hexColor,
					},
					{
						id: 2,
						label: cap(getTicketTypeMeta('tow').pluralLabel),
						value: parseInt(stats.towTickets),
						color: getTicketTypeMeta('tow').hexColor,
					},
				]);
			} catch {
				if (!cancelled) setError('Failed to load data');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchData();
		return () => {
			cancelled = true;
		};
	}, [parkingLocation.id, yearOffset, getTicketTypeMeta]);

	const year = new Date().getFullYear() - yearOffset;
	const isEmpty = data.every((d) => d.value === 0);

	return (
		<Card sx={{ width: '100%', maxWidth: 450 }}>
			<CardHeader title="Violations This Year" sx={{ pb: 0 }} />
			<CardContent sx={{ pt: 1 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						mb: 1,
						gap: 1,
					}}
				>
					<Tooltip title="Previous year">
						<IconButton
							size="small"
							onClick={() => setYearOffset(yearOffset + 1)}
						>
							<ChevronLeft />
						</IconButton>
					</Tooltip>
					<Typography variant="caption" color="text.secondary">
						{year}
					</Typography>
					<Tooltip
						title={
							yearOffset === 0
								? 'Already at the latest year'
								: 'Next year'
						}
					>
						<span>
							<IconButton
								size="small"
								onClick={() => setYearOffset(yearOffset - 1)}
								disabled={yearOffset === 0}
							>
								<ChevronRight />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
				{loading ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: CHART_HEIGHT,
						}}
					>
						<CircularProgress />
					</Box>
				) : error ? (
					<Typography
						color="error"
						sx={{ textAlign: 'center', py: 4 }}
					>
						{error}
					</Typography>
				) : isEmpty ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: CHART_HEIGHT,
						}}
					>
						<Typography variant="body2" color="text.secondary">
							No violations this year
						</Typography>
					</Box>
				) : (
					<PieChart
						height={CHART_HEIGHT}
						series={[
							{
								data,
								innerRadius: 60,
								outerRadius: 100,
								paddingAngle: 4,
								cornerRadius: 6,
								arcLabel: (item) =>
									`${item.label}: ${item.value}`,
								arcLabelMinAngle: 25,
							},
						]}
						margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
					/>
				)}
			</CardContent>
		</Card>
	);
};

export default CustomPieChart;
